import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import {
  PopoverMenu,
  PopoverMenuButton,
  PopoverMenuItem,
  PopoverMenuItemButton,
  PopoverMenuItems,
  PopoverMenuSection,
} from 'design-system/src/components/PopoverMenu/PopoverMenu';
import HorizontalMenu from 'design-system/src/assets/icons/horizontalMenu.svg?react';
import { Table, TableCell, TableContainer } from '../Table';
import PhoneIcon from 'design-system/src/assets/icons/phoneOutline.svg?react';
import { client } from '../../api';
import AddSinglePhoneNumberModal from './AddSinglePhoneNumberModal';
import RemovePhoneNumberModal from './RemovePhoneNumberModal';
import { formatPhoneNumber } from 'shared-utils/utils/formatPhoneNumber';

const CustomTableContainer = styled(TableContainer)`
  width: 100%;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--grayscale-100);

  &:last-child {
    border-bottom: none;
  }
`;

const CustomTableCell = styled(TableCell)`
  padding: 12px 0;
  vertical-align: middle;
`;

const PhoneNumberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PhoneIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    width: 16px;
    height: 16px;
    color: var(--grayscale-600);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 0;
  color: var(--grayscale-600);
`;

type PhoneNumberData = {
  phone_numbers: string[];
};

/**
 * Component for managing office phone numbers in settings.
 */
const OfficePhoneNumbers: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [phoneNumberToRemove, setPhoneNumberToRemove] = useState<string | null>(null);

  const { data, error, isPending } = useQuery<PhoneNumberData>({
    queryKey: ['office-phone-numbers'],
    queryFn: () => {
      return client.get('settings/phone-numbers').then((res) => res.data);
    },
  });

  if (isPending) {
    return <LoadingSpinner height="200px" />;
  } else if (error || !data) {
    return <PageLoadError />;
  }

  const phoneNumbers = data.phone_numbers || [];

  return (
    <>
      <ButtonRow align={Alignment.SpaceBetween}>
        <div>
          <Typography styledAs="bodyLargeDMSans" weight={600}>
            Office phone numbers
          </Typography>
          <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)" marginTop="4px">
            Tandem uses phone numbers to identify your incoming prescriptions.
          </Typography>
        </div>
        <Button size="lg" onClick={() => setShowAddModal(true)}>
          Add phone number
        </Button>
      </ButtonRow>

      <CustomTableContainer>
        {phoneNumbers.length === 0 ? (
          <EmptyState>
            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
              No phone numbers added yet. Click "Add phone number" to get started.
            </Typography>
          </EmptyState>
        ) : (
          <Table>
            <thead className="sr-only">
              <tr>
                <th>Phone number</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {phoneNumbers.map((phoneNumber, index) => (
                <TableRow key={index}>
                  <CustomTableCell>
                    <PhoneNumberRow>
                      <PhoneIconWrapper>
                        <PhoneIcon aria-hidden />
                      </PhoneIconWrapper>
                      <Typography styledAs="bodySmallDMSans" weight={600}>
                        {formatPhoneNumber(phoneNumber)}
                      </Typography>
                    </PhoneNumberRow>
                  </CustomTableCell>
                  <CustomTableCell className="w-12 text-right">
                    <PopoverMenu>
                      <PopoverMenuButton
                        aria-label={`Actions for ${formatPhoneNumber(phoneNumber)}`}
                        className="flex size-8 cursor-pointer items-center justify-center rounded-medium border-0 bg-transparent hover:bg-grayscale-50"
                      >
                        <HorizontalMenu aria-hidden />
                      </PopoverMenuButton>
                      <PopoverMenuItems width="200px" anchor="bottom end">
                        <PopoverMenuSection>
                          <PopoverMenuItem>
                            {(itemProps) => (
                              <PopoverMenuItemButton
                                {...itemProps}
                                className="text-red-400"
                                onClick={() => {
                                  itemProps.close?.();
                                  setPhoneNumberToRemove(phoneNumber);
                                }}
                              >
                                Remove phone number
                              </PopoverMenuItemButton>
                            )}
                          </PopoverMenuItem>
                        </PopoverMenuSection>
                      </PopoverMenuItems>
                    </PopoverMenu>
                  </CustomTableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </CustomTableContainer>

      {showAddModal && (
        <AddSinglePhoneNumberModal onClose={() => setShowAddModal(false)} existingNumbers={phoneNumbers} />
      )}
      {phoneNumberToRemove && (
        <RemovePhoneNumberModal phoneNumber={phoneNumberToRemove} onClose={() => setPhoneNumberToRemove(null)} />
      )}
    </>
  );
};

export default OfficePhoneNumbers;
